import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { Tick, ConnectionState, UserEvent } from '@dabba/contracts';
import { SocketMarketDataSource } from './SocketMarketDataSource';
import { WS_URL } from '../api/config';
import { useSession } from '../store/session';

const SourceCtx = createContext<SocketMarketDataSource | null>(null);

/**
 * One gateway connection per tab, owned by the module rather than by a
 * component.
 *
 * It used to be `useMemo(() => new SocketMarketDataSource(...), [])` with a
 * `dispose()` on unmount, which is wrong under StrictMode: React deliberately
 * mounts, unmounts and remounts every effect in development, so the cleanup
 * disposed the transport and the remount reused the same — now permanently
 * disposed — instance. The app sat on "Offline" forever with no reconnect and
 * no error, and every price rendered as a dash.
 *
 * A tab has exactly one socket for its whole life, so module scope is where it
 * belongs. Nothing disposes it: the page unload closes it, which is the only
 * moment it should actually go away.
 */
let sharedSource: SocketMarketDataSource | null = null;
function getSource(): SocketMarketDataSource {
  sharedSource ??= new SocketMarketDataSource(WS_URL);
  return sharedSource;
}

/**
 * The same per-tab transport, for code that lives outside the React tree —
 * the TradingView datafeed adapter is constructed once at module scope and
 * cannot call a hook. Sharing the singleton keeps its live-bar ticks on the
 * exact stream every other screen renders, refcounts included.
 */
export function getMarketSource(): SocketMarketDataSource {
  return getSource();
}

export function DataProvider({ children }: { children: ReactNode }) {
  const source = useMemo(getSource, []);

  // Keep the connection's user identity in step with the session, so personal
  // events (fills, challenge updates) arrive on this same socket.
  const token = useSession((s) => s.token);
  useEffect(() => {
    source.setAuthToken(token);
  }, [source, token]);

  // Browsers throttle background tabs and may drop the socket without firing
  // onclose — kick on refocus so a returning tab never sits on a dead socket.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') source.kick();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onVisible);
    };
  }, [source]);

  return <SourceCtx.Provider value={source}>{children}</SourceCtx.Provider>;
}

function useSource(): SocketMarketDataSource {
  const s = useContext(SourceCtx);
  if (!s) throw new Error('useSource must be used within <DataProvider>');
  return s;
}

/**
 * Subscribe to a symbol and get its latest tick. Returns the retained last
 * value immediately, then live updates. On web, routes unmount when left, so
 * mount-scoped subscriptions are the equivalent of mobile's focus scoping.
 */
export function useTick(symbol: string | null): Tick | null {
  const source = useSource();
  const [tick, setTick] = useState<Tick | null>(() => (symbol ? source.peek(symbol) : null));

  useEffect(() => {
    if (!symbol) return;
    setTick(source.peek(symbol));
    source.subscribe([symbol]);
    const off = source.onTick((t) => {
      if (t.symbol === symbol) setTick(t);
    });
    return () => {
      off();
      source.unsubscribe([symbol]);
    };
  }, [source, symbol]);

  return tick;
}

/**
 * Live ticks for a whole set of symbols, keyed by symbol — one subscription
 * for the list rather than one per row.
 */
export function useTicks(symbols: string[]): Record<string, Tick> {
  const source = useSource();
  // Symbol arrays are rebuilt every render; the joined string is the real dep.
  const key = symbols.join(',');
  const [ticks, setTicks] = useState<Record<string, Tick>>({});

  useEffect(() => {
    const list = key ? key.split(',') : [];
    if (list.length === 0) {
      setTicks({});
      return;
    }
    const seed: Record<string, Tick> = {};
    for (const s of list) {
      const t = source.peek(s);
      if (t) seed[s] = t;
    }
    setTicks(seed);

    const wanted = new Set(list);
    source.subscribe(list);
    const off = source.onTick((t) => {
      if (wanted.has(t.symbol)) setTicks((prev) => ({ ...prev, [t.symbol]: t }));
    });
    return () => {
      off();
      source.unsubscribe(list);
    };
  }, [source, key]);

  return ticks;
}

/** Personal push events for the signed-in user (order fills, challenge state). */
export function useUserEvents(cb: (e: UserEvent) => void): void {
  const source = useSource();
  const ref = useRef(cb);
  ref.current = cb;
  useEffect(() => source.onEvent((e) => ref.current(e)), [source]);
}

export function useConnectionState(): ConnectionState {
  const source = useSource();
  const [state, setState] = useState<ConnectionState>(() => source.connectionState());
  useEffect(() => source.onConnectionState(setState), [source]);
  return state;
}

/**
 * The honest-price rule, centralized: are prices trustworthy right now?
 * Screens use this to lock order entry.
 */
export function usePricesLive(): boolean {
  const state = useConnectionState();
  return state === 'live';
}
